import { useEffect, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'

import { useGetUpdatedLogs } from '../api/useGetUpdatedLogs'
import { useGetControllerLogs } from '@/modules/CombinedLogs'
import { useResetUpdatedRecords } from '../api/useResetUpdatedRecords'
import { useAuthStore } from '@/store/auth-store'
import { Role } from '@/types/auth'
import { LogSource } from '@/types/logs'
import { useGarageStore } from '@/modules/FindGarage'

export const useUpdatedRecordsAvailable = () => {
    const { data: updatedLogs = [] } = useGetUpdatedLogs()
    const { data: logs = [] } = useGetControllerLogs({
        isUnfinished: true,
        source: LogSource.Manual,
        useAreaRoutes: true,
    })
    const { role } = useAuthStore()
    const { garage } = useGarageStore()
    const { handleResetUpdatedRecords } = useResetUpdatedRecords()
    const location = useLocation()
    const previousRoute = useRef(location.pathname)

    const haveUpdatedRecords = useMemo(() => {
        const updated = updatedLogs.some((item) => item.isUpdatedAfterUnfinished)

        if (role === Role.MileageClerk) {
            return garage && updated
        }

        return updated || (!!logs.length && logs.some((item) => item.isUpdatedAfterUnfinished))
    }, [updatedLogs, logs, garage])

    useEffect(() => {
        if (role && ![Role.MileageClerk, Role.PerformanceManager].includes(role)) {
            return
        }

        if (previousRoute.current === '/records' && haveUpdatedRecords) {
            const updatedRecordIds = Array.from(
                new Set([
                    ...updatedLogs.reduce<string[]>(
                        (acc, item) => (item.isUpdatedAfterUnfinished ? [...acc, item.id] : acc),
                        []
                    ),
                ])
            )

            if (role === Role.PerformanceManager) {
                updatedRecordIds.push(
                    ...Array.from(
                        new Set([
                            ...logs.reduce<string[]>(
                                (acc, item) => (item.isUpdatedAfterUnfinished ? [...acc, item.id] : acc),
                                []
                            ),
                        ])
                    )
                )
            }
            handleResetUpdatedRecords(updatedRecordIds.map((id) => ({ id, isUpdated: false })))
        }

        previousRoute.current = location.pathname
    }, [location, haveUpdatedRecords, role])

    return haveUpdatedRecords
}
